/**
 * Fallback for snippets/article-related-products.liquid.
 *
 * When Liquid could not resolve a collection for the article (no metafield, no tag
 * matching a collection), this queries predictive search for the article title and
 * renders up to `data-limit` available products. If nothing matches, the container
 * stays empty and hidden.
 *
 * Runs once per page, after idle, so it never competes with the article's LCP.
 */
(() => {
  const roots = document.querySelectorAll('[data-article-related-products]:not([data-resolved])');
  if (!roots.length) return;

  const formatPrice = (value) => {
    const amount = Number(value);
    if (Number.isNaN(amount)) return '';
    try {
      return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(
        amount
      );
    } catch {
      return `Rp ${Math.round(amount).toLocaleString('id-ID')}`;
    }
  };

  const escapeHtml = (text) =>
    String(text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const imageUrl = (product) => {
    const src = product.featured_image?.url || product.image;
    if (!src) return '';
    // Shopify CDN accepts a width param; keep it small for a 4-up grid.
    return src.includes('?') ? `${src}&width=400` : `${src}?width=400`;
  };

  const render = async (root) => {
    const query = root.dataset.query?.trim();
    const limit = Math.min(Math.max(parseInt(root.dataset.limit || '4', 10) || 4, 1), 8);
    const searchUrl = root.dataset.searchUrl || '/search/suggest';
    if (!query) return;

    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product',
      'resources[limit]': String(limit * 2),
      'resources[options][unavailable_products]': 'hide',
    });

    let products = [];
    try {
      const res = await fetch(`${searchUrl}.json?${params.toString()}`, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const data = await res.json();
      products = data?.resources?.results?.products || [];
    } catch (error) {
      console.error('Related products search failed:', error);
      return;
    }

    products = products.filter((p) => p.available !== false).slice(0, limit);
    if (!products.length) return;

    const template = root.querySelector('[data-related-template]');
    if (!template) return;
    const fragment = template.content.cloneNode(true);
    const grid = fragment.querySelector('[data-related-grid]');

    grid.innerHTML = products
      .map((p) => {
        const img = imageUrl(p);
        return `<li class="article-related-products__item">
          <a class="article-related-products__card" href="${escapeHtml(p.url)}">
            <span class="article-related-products__media">${
              img ? `<img src="${escapeHtml(img)}" alt="${escapeHtml(p.title)}" loading="lazy" width="400" height="400">` : ''
            }</span>
            <span class="article-related-products__title">${escapeHtml(p.title)}</span>
            <span class="article-related-products__price">${formatPrice(p.price)}</span>
          </a>
        </li>`;
      })
      .join('');

    template.remove();
    root.appendChild(fragment);
    root.dataset.resolved = 'client';
  };

  const start = () => roots.forEach(render);
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(start, { timeout: 2000 });
  } else {
    setTimeout(start, 300);
  }
})();
